import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  Theme,
  useTheme
} from "@mui/material";
import { GetCurrentConsentApplicationBody } from "../../redux-store/actions/otpConsent";
import { DISABLE_NOMINEE_OPY_OUT } from "../../utils/constant";
import ConsentBankNominee from "./ConsentBankNominee";

function ConsentInfo({
  showOtp,
  handleSendOtp,
  currentApplicationData = {},
  isJointHolder
}: {
  showOtp: boolean;
  handleSendOtp: () => void;
  currentApplicationData: Partial<GetCurrentConsentApplicationBody>;
  isJointHolder: boolean;
}): JSX.Element {
  const theme = useTheme();

  const jointHolders = [
    {
      name: currentApplicationData?.secondHolder,
      pan: currentApplicationData?.secondHolderPan
    },
    {
      name: currentApplicationData?.thirdHolder,
      pan: currentApplicationData?.thirdHolderPan
    }
  ].filter(
    (h) =>
      h.name &&
      h.pan &&
      h.name.trim() !== "" &&
      h.pan.trim() !== ""
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        width: "100%",
        maxWidth: '760px',
        borderRadius: "14px",
        backgroundColor: "background.paper",
        overflowX: "auto"
      }}
    >
      {/* Header */}
      <Typography
        variant="h2"
        fontWeight={700}
        sx={{ mb: 2, color: "text.primary", textAlign: "center" }}
      >
        Consent for Adding Joint Holder(s)
      </Typography>

      {/* Holder Info Cards */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, width: "100%" }}
      >
        {[
          { label: "Holder Name", value: currentApplicationData?.name },
          {
            label: "Application Number",
            value: currentApplicationData?.applicationNumber
          },
          { label: "PAN Number", value: currentApplicationData?.panNo }
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[50],
              textAlign: "center",
              border: `1px solid ${theme.palette.grey[200]}`
            }}
          >
            <Typography
              variant="subtitle_500"
              fontWeight={600}
              color="text.secondary"
            >
              {item.label}
            </Typography>
            <Typography
              variant="subtitle_400"
              fontWeight={600}
              color="text.primary"
              sx={{
                wordBreak: "break-word",
                mt: 0.5
              }}
            >
              {item.value || "—"}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Consent Info */}
      <Typography
        variant="subtitle_300"
        color="text.primary"
        textAlign="center"
        fontWeight={500}
        sx={{ mb: 2 }}
      >
        {isJointHolder
          ? "By verifying the OTP, you are giving consent to be added as a joint holder."
          : DISABLE_NOMINEE_OPY_OUT ? "By verifying the OTP, you are giving consent to add the Joint Holders" : "By verifying the OTP, you are giving consent to add the Joint Holders and Nominees."}
      </Typography>

      {/* Joint Holders Section */}
      <Box sx={{ mb: 2 }}>
      {!isJointHolder ? (
          <Stack spacing={1}>
            {/* Joint Holders */}
            {jointHolders.length > 0 ? (
              jointHolders.map((h, i) => (
                <Typography key={i} variant="subtitle_400" color="primary.800">
                  Joint Holder {i + 1}: <strong>{h.name}</strong> (PAN: {h.pan})
                </Typography>
              ))
            ) : (
              <Typography color="text.secondary" sx={{ ...theme.typography.subtitle_400 }}>
                No joint holders added.
              </Typography>
            )}
             {/* Nominee Section */}
            {/* {!DISABLE_NOMINEE_OPY_OUT && (((currentApplicationData?.nominees || []).length > 0) ? (
              <NomineeTable
                data={
                  (currentApplicationData?.nominees ||
                    []) as NomineeType[]
                }
              />
            ) : (
              <Typography
                color="text.secondary"
                sx={{ mt: 1, ...theme.typography.subtitle_400 }}
              >
                {NO_NOMINEE_DISCLAIMER}
              </Typography>
            ))}

            //Bank section
            {
              (((currentApplicationData?.banks||[]).length>0)?(
                <BanksTable
                data={(currentApplicationData?.banks||[]) as Bank[]
                }
                />
              ):
              (
               <Typography
                color="text.secondary"
                sx={{ mt: 1, ...theme.typography.subtitle_400 }}
              >
                {NO_BANKS_ADDED}
              </Typography>
              ))
            } */}
          </Stack>
      ) : (
        <Typography variant="body1" color="primary.800">
            Primary Holder:{" "}
            <strong>{currentApplicationData?.primaryHolder}</strong> (PAN:{" "}
            {currentApplicationData?.primaryHolderPan})
          </Typography>    
        )}
        </Box>

      {/* Nominees + Banks */}
      <Box sx={{ mb: 2 }}>
        <ConsentBankNominee
          currentApplicationData={currentApplicationData}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* OTP Section */}
      <Typography
        variant="body_500"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 1 }}
      >
        An OTP will be sent to:
      </Typography>

      <Stack spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body_500" color="text.primary">
          Mobile:{" "}
          <strong>{currentApplicationData?.countryCode || "—"}-{currentApplicationData?.mobile || "—"}</strong>
        </Typography>
        <Typography variant="body_500" color="text.primary">
          Email:{" "}
          <strong>{currentApplicationData?.email || "—"}</strong>
        </Typography>
      </Stack>

      {/* Button */}
      <Box textAlign="center">
        <Button
          variant="contained"
          onClick={handleSendOtp}
          sx={{
            px: 4,
            py: 1.2,
            fontWeight: 600,
            backgroundColor: "primary.600",
            ":hover": { backgroundColor: "primary.800" }
          }}
        >
          Send OTP
        </Button>
      </Box>
    </Paper>
  );
}

export default ConsentInfo;
